import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import {
  buildOperationalOrdersStorageKey,
  getOperationalOrdersState,
  OPERATIONAL_ORDERS_UPDATED_EVENT,
} from "../../services/local/operationalOrders";
import {
  buildPaymentOperationsStorageKey,
  getPaymentOperations,
  PAYMENT_OPERATIONS_UPDATED_EVENT,
} from "../../services/local/paymentOperations";
import { getBranchCommissionDebtSummary } from "../../services/supabase/commissionLedgerService";
import { hydrateSharedOperationalStateToLocal } from "../../services/supabase/operationalStateService";
import {
  buildStatisticsSnapshot,
  STATISTICS_RANGE_OPTIONS,
} from "../../services/local/statisticsService";

const formatCurrency = (value) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatCompactNumber = (value) =>
  new Intl.NumberFormat("es-AR", {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

function Estadisticas() {
  const {
    currentBranch,
    currentRestaurant,
    isCreatorMode,
    isSupabaseConfigured,
  } = useAuth();
  const activeBranchScope = currentBranch || currentRestaurant;
  const [rangeId, setRangeId] = useState("all");
  const [ordersState, setOrdersState] = useState(() =>
    getOperationalOrdersState(activeBranchScope, () => ({
      mesasActivas: [],
      pedidosHistoricos: [],
    }))
  );
  const [paymentOperations, setPaymentOperations] = useState(() =>
    getPaymentOperations(activeBranchScope)
  );
  const [ledgerDebtAmount, setLedgerDebtAmount] = useState(null);

  useEffect(() => {
    setOrdersState(
      getOperationalOrdersState(activeBranchScope, () => ({
        mesasActivas: [],
        pedidosHistoricos: [],
      }))
    );
    setPaymentOperations(getPaymentOperations(activeBranchScope));
  }, [activeBranchScope]);

  useEffect(() => {
    const restaurantId = activeBranchScope?.id || "default";

    const syncOrders = () => {
      setOrdersState(
        getOperationalOrdersState(activeBranchScope, () => ({
          mesasActivas: [],
          pedidosHistoricos: [],
        }))
      );
    };

    const syncPayments = () => {
      setPaymentOperations(getPaymentOperations(activeBranchScope));
    };

    const syncLedgerDebt = async () => {
      if (!activeBranchScope?.id || isCreatorMode || !isSupabaseConfigured) {
        setLedgerDebtAmount(null);
        return;
      }

      try {
        const summary = await getBranchCommissionDebtSummary(activeBranchScope);
        setLedgerDebtAmount(
          Number.isFinite(Number(summary?.outstandingDebtAmount))
            ? Number(summary.outstandingDebtAmount)
            : 0
        );
      } catch (error) {
        console.error("No pudimos actualizar la deuda acumulada de la sucursal:", error);
      }
    };

    const handleOrdersUpdate = (event) => {
      const updatedRestaurantId = event.detail?.restaurantId || "default";
      if (updatedRestaurantId !== restaurantId) {
        return;
      }

      setOrdersState(
        event.detail?.payload || {
          mesasActivas: [],
          pedidosHistoricos: [],
        }
      );
    };

    const handlePaymentsUpdate = (event) => {
      const updatedRestaurantId = event.detail?.restaurantId || "default";
      if (updatedRestaurantId !== restaurantId) {
        return;
      }

      setPaymentOperations(
        Array.isArray(event.detail?.operations)
          ? event.detail.operations
          : getPaymentOperations(activeBranchScope)
      );
      void syncLedgerDebt();
    };

    const handleStorage = (event) => {
      if (event.key === buildOperationalOrdersStorageKey(activeBranchScope?.id)) {
        syncOrders();
      }

      if (event.key === buildPaymentOperationsStorageKey(activeBranchScope?.id)) {
        syncPayments();
        void syncLedgerDebt();
      }
    };

    void syncLedgerDebt();

    window.addEventListener(OPERATIONAL_ORDERS_UPDATED_EVENT, handleOrdersUpdate);
    window.addEventListener(PAYMENT_OPERATIONS_UPDATED_EVENT, handlePaymentsUpdate);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(OPERATIONAL_ORDERS_UPDATED_EVENT, handleOrdersUpdate);
      window.removeEventListener(PAYMENT_OPERATIONS_UPDATED_EVENT, handlePaymentsUpdate);
      window.removeEventListener("storage", handleStorage);
    };
  }, [activeBranchScope, isCreatorMode, isSupabaseConfigured]);

  useEffect(() => {
    if (!activeBranchScope?.id || isCreatorMode || !isSupabaseConfigured) {
      return undefined;
    }

    let cancelled = false;

    const hydrateRemoteState = async () => {
      try {
        await hydrateSharedOperationalStateToLocal(activeBranchScope, () => ({
          mesasActivas: [],
          pedidosHistoricos: [],
        }));

        if (cancelled) {
          return;
        }

        setOrdersState(
          getOperationalOrdersState(activeBranchScope, () => ({
            mesasActivas: [],
            pedidosHistoricos: [],
          }))
        );
        setPaymentOperations(getPaymentOperations(activeBranchScope));
      } catch (error) {
        console.error(
          "No pudimos hidratar los pagos remotos para Estadisticas:",
          error
        );
      }
    };

    void hydrateRemoteState();

    return () => {
      cancelled = true;
    };
  }, [activeBranchScope, isCreatorMode, isSupabaseConfigured]);

  const snapshot = useMemo(
    () => buildStatisticsSnapshot(ordersState, paymentOperations, rangeId, new Date()),
    [ordersState, paymentOperations, rangeId]
  );
  const feesGeneratedLabel =
    rangeId === "all"
      ? "Comision generada acumulada"
      : "Comision generada en el periodo";

  const summaryCards = [
    {
      id: "orders",
      label: "Pedidos registrados",
      value: formatCompactNumber(snapshot.summary.ordersCount),
      detail: "Pedidos tomados en el periodo",
    },
    {
      id: "sales",
      label: "Venta bruta",
      value: formatCurrency(snapshot.summary.estimatedSales),
      detail: "Total de pedidos cargados",
    },
    {
      id: "collected",
      label: "Cobrado bruto",
      value: formatCurrency(snapshot.summary.collectedAmount),
      detail: "Cobros registrados en caja",
    },
    {
      id: "merchant-net",
      label: "Neto del local",
      value: formatCurrency(snapshot.summary.merchantNetSales),
      detail: "Venta despues de la comision base",
    },
  ];

  const operationalCards = [
    {
      id: "tables",
      label: "Mesas activas ahora",
      value: formatCompactNumber(snapshot.summary.activeTablesNow),
    },
    {
      id: "pending",
      label: "Pendientes ahora",
      value: formatCompactNumber(snapshot.summary.pendingNow),
    },
    {
      id: "ready",
      label: "Listos ahora",
      value: formatCompactNumber(snapshot.summary.readyNow),
    },
    {
      id: "payments",
      label: "Operaciones abiertas",
      value: formatCompactNumber(snapshot.summary.openPaymentOperationsNow),
    },
  ];

  const promoCards = [
    {
      id: "combo-discount",
      label: "Descuento por combos",
      value: formatCurrency(snapshot.summary.combosDiscount),
    },
    {
      id: "promo-discount",
      label: "Descuento por promos",
      value: formatCurrency(snapshot.summary.promotionsDiscount),
    },
  ];

  const economicsCards = [
    {
      id: "platform-fees",
      label: feesGeneratedLabel,
      value: formatCurrency(snapshot.summary.platformFeesGenerated),
    },
    {
      id: "retained",
      label: "Retenido por deeplink",
      value: formatCurrency(snapshot.summary.retainedAmount),
    },
    {
      id: "pending-debt",
      label: "Deuda pendiente acumulada",
      value: formatCurrency(
        ledgerDebtAmount === null ? snapshot.summary.pendingPlatformDebt : ledgerDebtAmount
      ),
    },
    {
      id: "ticket",
      label: "Ticket promedio",
      value: formatCurrency(snapshot.summary.averageTicket),
    },
  ];

  return (
    <div style={styles.page}>
      <section style={styles.headerBand}>
        <div>
          <div style={styles.overline}>Caja y estadisticas</div>
          <h1 style={styles.title}>Datos clave del restaurante</h1>
          <p style={styles.subtitle}>
            Aqui vemos por fecha, dias, montos, productos, canales y uso de promos con los
            datos operativos que ya va generando la sucursal.
          </p>
        </div>

        <div style={styles.rangeSelector}>
          {STATISTICS_RANGE_OPTIONS.map((option) => {
            const isActive = option.id === rangeId;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => setRangeId(option.id)}
                style={{
                  ...styles.rangeButton,
                  ...(isActive ? styles.rangeButtonActive : {}),
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </section>

      <section style={styles.metricsGrid}>
        {summaryCards.map((card) => (
          <article key={card.id} style={styles.metricCard}>
            <div style={styles.metricLabel}>{card.label}</div>
            <div style={styles.metricValue}>{card.value}</div>
            <div style={styles.metricDetail}>{card.detail}</div>
          </article>
        ))}
      </section>

      <section style={styles.secondaryGrid}>
        <div style={styles.block}>
          <div style={styles.blockTitle}>Operacion actual</div>
          <div style={styles.miniMetricsGrid}>
            {operationalCards.map((card) => (
              <article key={card.id} style={styles.miniMetricCard}>
                <div style={styles.miniMetricLabel}>{card.label}</div>
                <div style={styles.miniMetricValue}>{card.value}</div>
              </article>
            ))}
          </div>
        </div>

        <div style={styles.block}>
          <div style={styles.blockTitle}>Impacto comercial</div>
          <div style={styles.miniMetricsGrid}>
            {promoCards.map((card) => (
              <article key={card.id} style={styles.miniMetricCard}>
                <div style={styles.miniMetricLabel}>{card.label}</div>
                <div style={styles.miniMetricValue}>{card.value}</div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section style={styles.secondaryGrid}>
        <div style={styles.block}>
          <div style={styles.blockTitle}>Economia de cobros</div>
          <div style={styles.miniMetricsGrid}>
            {economicsCards.map((card) => (
              <article key={card.id} style={styles.miniMetricCard}>
                <div style={styles.miniMetricLabel}>{card.label}</div>
                <div style={styles.miniMetricValue}>{card.value}</div>
              </article>
            ))}
          </div>
        </div>

        <div style={styles.block}>
          <div style={styles.blockTitle}>Liquidez del local</div>
          <div style={styles.miniMetricsGrid}>
            <article style={styles.miniMetricCard}>
              <div style={styles.miniMetricLabel}>Transferido al local</div>
              <div style={styles.miniMetricValue}>
                {formatCurrency(snapshot.summary.merchantPayoutAmount)}
              </div>
            </article>
            <article style={styles.miniMetricCard}>
              <div style={styles.miniMetricLabel}>Cobros abiertos</div>
              <div style={styles.miniMetricValue}>
                {formatCompactNumber(snapshot.summary.openPaymentOperationsNow)}
              </div>
            </article>
          </div>
        </div>
      </section>

      <section style={styles.detailGrid}>
        <div style={styles.block}>
          <div style={styles.blockHeader}>
            <div style={styles.blockTitle}>Resumen por fecha</div>
            <div style={styles.blockHelper}>Pedidos, venta estimada y cobrado</div>
          </div>
          <div style={styles.listColumn}>
            {snapshot.dailyRows.length ? (
              snapshot.dailyRows.map((row) => (
                <article key={row.id} style={styles.rowCard}>
                  <div style={styles.rowPrimary}>
                    <div style={styles.rowTitle}>
                      {row.weekday} {row.label}
                    </div>
                    <div style={styles.rowMeta}>{row.ordersCount} pedidos</div>
                  </div>
                  <div style={styles.rowAmounts}>
                    <div style={styles.rowAmountLine}>
                      <span>Vendido</span>
                      <strong>{formatCurrency(row.salesAmount)}</strong>
                    </div>
                    <div style={styles.rowAmountLine}>
                      <span>Cobrado</span>
                      <strong>{formatCurrency(row.collectedAmount)}</strong>
                    </div>
                    <div style={styles.rowAmountLine}>
                      <span>Comision</span>
                      <strong>{formatCurrency(row.platformFeesAmount)}</strong>
                    </div>
                    <div style={styles.rowAmountLine}>
                      <span>Neto local</span>
                      <strong>{formatCurrency(row.netAmount)}</strong>
                    </div>
                  </div>
                </article>
              ))
            ) : (
              <div style={styles.emptyState}>Todavia no hay movimiento suficiente para este periodo.</div>
            )}
          </div>
        </div>

        <div style={styles.block}>
          <div style={styles.blockHeader}>
            <div style={styles.blockTitle}>Canales y cobro</div>
            <div style={styles.blockHelper}>Como estan entrando y cobrando los pedidos</div>
          </div>
          <div style={styles.splitColumn}>
            <div style={styles.subBlock}>
              <div style={styles.subBlockTitle}>Canales</div>
              <div style={styles.listColumn}>
                {snapshot.channelBreakdown.length ? (
                  snapshot.channelBreakdown.map((channel) => (
                    <article key={channel.id} style={styles.rowCard}>
                      <div style={styles.rowPrimary}>
                        <div style={styles.rowTitle}>{channel.label}</div>
                        <div style={styles.rowMeta}>{channel.ordersCount} pedidos</div>
                      </div>
                      <strong style={styles.rowStrong}>{formatCurrency(channel.amount)}</strong>
                    </article>
                  ))
                ) : (
                  <div style={styles.emptyState}>Sin pedidos cargados en este periodo.</div>
                )}
              </div>
            </div>

            <div style={styles.subBlock}>
              <div style={styles.subBlockTitle}>Medios de cobro</div>
              <div style={styles.listColumn}>
                {snapshot.paymentMethodBreakdown.length ? (
                  snapshot.paymentMethodBreakdown.map((method) => (
                    <article key={method.id} style={styles.rowCard}>
                      <div style={styles.rowPrimary}>
                        <div style={styles.rowTitle}>{method.label}</div>
                        <div style={styles.rowMeta}>{method.count} cobros</div>
                      </div>
                      <div style={styles.rowAmounts}>
                        <div style={styles.rowAmountLine}>
                          <span>Bruto</span>
                          <strong>{formatCurrency(method.amount)}</strong>
                        </div>
                        <div style={styles.rowAmountLine}>
                          <span>Comision</span>
                          <strong>{formatCurrency(method.feeAmount)}</strong>
                        </div>
                        <div style={styles.rowAmountLine}>
                          <span>Deuda</span>
                          <strong>{formatCurrency(method.debtAmount)}</strong>
                        </div>
                      </div>
                    </article>
                  ))
                ) : (
                  <div style={styles.emptyState}>Todavia no hay cobros registrados en el periodo.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={styles.detailGrid}>
        <div style={styles.block}>
          <div style={styles.blockHeader}>
            <div style={styles.blockTitle}>Productos mas pedidos</div>
            <div style={styles.blockHelper}>Cantidad y facturacion estimada</div>
          </div>
          <div style={styles.listColumn}>
            {snapshot.topProducts.length ? (
              snapshot.topProducts.map((product, index) => (
                <article key={product.id} style={styles.rowCard}>
                  <div style={styles.rankBadge}>{index + 1}</div>
                  <div style={{ ...styles.rowPrimary, flex: 1 }}>
                    <div style={styles.rowTitle}>{product.name}</div>
                    <div style={styles.rowMeta}>{product.quantity} unidades</div>
                  </div>
                  <strong style={styles.rowStrong}>{formatCurrency(product.amount)}</strong>
                </article>
              ))
            ) : (
              <div style={styles.emptyState}>Todavia no hay productos vendidos en este periodo.</div>
            )}
          </div>
        </div>

        <div style={styles.block}>
          <div style={styles.blockHeader}>
            <div style={styles.blockTitle}>Promos y combos</div>
            <div style={styles.blockHelper}>Veces usados y descuento acumulado</div>
          </div>
          <div style={styles.listColumn}>
            {snapshot.adjustments.length ? (
              snapshot.adjustments.map((adjustment) => (
                <article key={adjustment.id} style={styles.rowCard}>
                  <div style={styles.rowPrimary}>
                    <div style={styles.rowTitle}>{adjustment.title}</div>
                    <div style={styles.rowMeta}>
                      {adjustment.type === "combo" ? "Combo" : "Promo"} · {adjustment.uses} usos
                    </div>
                  </div>
                  <strong style={styles.rowStrong}>{formatCurrency(adjustment.amount)}</strong>
                </article>
              ))
            ) : (
              <div style={styles.emptyState}>No se aplicaron promos ni combos en este periodo.</div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const styles = {
  page: {
    display: "grid",
    gap: "22px",
  },
  headerBand: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: "18px",
    flexWrap: "wrap",
    paddingBottom: "6px",
  },
  overline: {
    color: "var(--brand-primary, #2563eb)",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    fontSize: "12px",
    fontWeight: 800,
    marginBottom: "8px",
  },
  title: {
    margin: 0,
    color: "#0f172a",
    fontSize: "36px",
    fontWeight: 800,
    lineHeight: 1.05,
  },
  subtitle: {
    margin: "12px 0 0",
    maxWidth: "820px",
    color: "#475569",
    fontSize: "15px",
    lineHeight: 1.6,
  },
  rangeSelector: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },
  rangeButton: {
    borderRadius: "999px",
    border: "1px solid var(--brand-border, #cbd5e1)",
    backgroundColor: "#ffffff",
    color: "#334155",
    padding: "10px 16px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
  },
  rangeButtonActive: {
    backgroundColor: "var(--brand-primary, #2563eb)",
    color: "#ffffff",
    borderColor: "var(--brand-primary, #2563eb)",
    boxShadow: "0 14px 32px rgba(37, 99, 235, 0.22)",
  },
  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: "16px",
  },
  metricCard: {
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
    border: "1px solid var(--brand-border, #dbeafe)",
    borderRadius: "18px",
    padding: "20px",
    boxShadow: "0 16px 36px rgba(15, 23, 42, 0.07)",
  },
  metricLabel: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: 700,
    marginBottom: "10px",
  },
  metricValue: {
    color: "#0f172a",
    fontSize: "34px",
    fontWeight: 800,
    lineHeight: 1,
    marginBottom: "10px",
  },
  metricDetail: {
    color: "var(--brand-primary, #2563eb)",
    fontSize: "13px",
    fontWeight: 700,
  },
  secondaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: "18px",
  },
  block: {
    display: "grid",
    gap: "14px",
  },
  blockHeader: {
    display: "grid",
    gap: "6px",
  },
  blockTitle: {
    color: "#0f172a",
    fontSize: "26px",
    fontWeight: 800,
  },
  blockHelper: {
    color: "#64748b",
    fontSize: "14px",
    fontWeight: 600,
  },
  miniMetricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "12px",
  },
  miniMetricCard: {
    backgroundColor: "rgba(255,255,255,0.94)",
    border: "1px solid var(--brand-border, #e2e8f0)",
    borderRadius: "16px",
    padding: "16px",
    minHeight: "100px",
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
  },
  miniMetricLabel: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: 700,
    marginBottom: "12px",
  },
  miniMetricValue: {
    color: "var(--brand-primary, #2563eb)",
    fontSize: "28px",
    fontWeight: 800,
    lineHeight: 1,
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: "22px",
  },
  listColumn: {
    display: "grid",
    gap: "12px",
  },
  splitColumn: {
    display: "grid",
    gap: "18px",
  },
  subBlock: {
    display: "grid",
    gap: "12px",
  },
  subBlockTitle: {
    color: "#0f172a",
    fontSize: "17px",
    fontWeight: 800,
  },
  rowCard: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "16px 18px",
    borderRadius: "16px",
    border: "1px solid rgba(148, 163, 184, 0.22)",
    backgroundColor: "rgba(255,255,255,0.95)",
    boxShadow: "0 10px 22px rgba(15, 23, 42, 0.04)",
  },
  rowPrimary: {
    display: "grid",
    gap: "4px",
  },
  rowTitle: {
    color: "#0f172a",
    fontSize: "15px",
    fontWeight: 800,
  },
  rowMeta: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: 600,
  },
  rowAmounts: {
    marginLeft: "auto",
    display: "grid",
    gap: "6px",
    minWidth: "180px",
  },
  rowAmountLine: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    color: "#475569",
    fontSize: "13px",
    fontWeight: 600,
  },
  rowStrong: {
    marginLeft: "auto",
    color: "#0f172a",
    fontSize: "15px",
    fontWeight: 800,
  },
  rankBadge: {
    width: "34px",
    height: "34px",
    borderRadius: "999px",
    backgroundColor: "var(--brand-soft, #dbeafe)",
    color: "var(--brand-primary, #2563eb)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    flexShrink: 0,
  },
  emptyState: {
    padding: "18px",
    borderRadius: "16px",
    border: "1px dashed rgba(148, 163, 184, 0.35)",
    color: "#64748b",
    backgroundColor: "rgba(255,255,255,0.7)",
    fontSize: "14px",
    fontWeight: 600,
  },
};

export default Estadisticas;
